import Pusher from 'pusher-js';
import { useEffect, useRef, useState } from 'react';
import type { Operaciones } from '../../api/operaciones';
import { leerConfiguracion } from '../../config';

/**
 * Tiempo real de la Sala (G463): `createFloorRealtimeTicket` da el canal privado de la sede y un ticket de un solo uso;
 * al suscribirse, la firma se pide con `authorizeFloorRealtimeChannel` (cada suscripción, un ticket nuevo). El canal solo
 * trae avisos ligeros (`sala.cambio`: tipo, id y estado, sin datos personales): con cada uno se vuelve a leer la foto.
 * Si no se puede conectar o la conexión cae, la Sala lee `getFloorLiveState` cada 30 s y lo dice («Cada 30 s»).
 */
export type Conexion = 'directo' | 'sondeo';

export const SONDEO_MS = 30_000;

export function useSalaEnVivo(ops: Operaciones, negocio: string, planId: string | null, alCambiar: () => void): Conexion {
    const [conexion, setConexion] = useState<Conexion>('sondeo');
    const cambio = useRef(alCambiar);
    useEffect(() => {
        cambio.current = alCambiar;
    }, [alCambiar]);

    // Conexión al canal de la sede.
    useEffect(() => {
        const reverb = leerConfiguracion().reverb;
        if (planId === null || reverb.clave === '') {
            return;
        }
        let vivo = true;
        let cliente: Pusher | null = null;
        let espera: ReturnType<typeof setTimeout> | null = null;
        const avisar = () => {
            if (espera !== null) {
                clearTimeout(espera);
            }
            espera = setTimeout(() => {
                cambio.current();
            }, 300);
        };
        ops.createFloorRealtimeTicket(negocio, planId)
            .then((primero) => {
                if (!vivo) {
                    return;
                }
                let ticket: string | null = primero.ticket;
                cliente = new Pusher(reverb.clave, {
                    cluster: '',
                    wsHost: reverb.host,
                    wsPort: reverb.puerto,
                    wssPort: reverb.puerto,
                    forceTLS: reverb.tls,
                    enabledTransports: ['ws', 'wss'],
                    channelAuthorization: {
                        endpoint: '',
                        transport: 'ajax',
                        customHandler: ({ socketId, channelName }, callback) => {
                            const usar = ticket;
                            ticket = null;
                            (usar === null ? ops.createFloorRealtimeTicket(negocio, planId).then((t) => t.ticket) : Promise.resolve(usar))
                                .then((t) => ops.authorizeFloorRealtimeChannel(negocio, { ticket: t, socket_id: socketId, channel_name: channelName }))
                                .then((firma) => { callback(null, firma); })
                                .catch((e: unknown) => {
                                    callback(e instanceof Error ? e : new Error(String(e)), null);
                                });
                        },
                    },
                });
                cliente.connection.bind('state_change', (s: { current: string }) => {
                    if (vivo) {
                        setConexion(s.current === 'connected' ? 'directo' : 'sondeo');
                        if (s.current === 'connected') {
                            avisar();
                        }
                    }
                });
                const canal = cliente.subscribe(primero.channel);
                canal.bind('sala.cambio', avisar);
                canal.bind('pusher:subscription_error', () => {
                    if (vivo) {
                        setConexion('sondeo');
                    }
                });
            })
            .catch(() => {
                if (vivo) {
                    setConexion('sondeo');
                }
            });
        return () => {
            vivo = false;
            if (espera !== null) {
                clearTimeout(espera);
            }
            cliente?.disconnect();
        };
    }, [ops, negocio, planId]);

    const efectiva: Conexion = planId !== null && leerConfiguracion().reverb.clave !== '' ? conexion : 'sondeo';

    // Sin conexión: la foto cada 30 s.
    useEffect(() => {
        if (efectiva === 'directo' || planId === null) {
            return;
        }
        const t = setInterval(() => {
            cambio.current();
        }, SONDEO_MS);
        return () => {
            clearInterval(t);
        };
    }, [efectiva, planId]);

    return efectiva;
}
